import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { HelpContentType } from "@deskpro-product/messenger-api-schemas/messenger/config/card-components";
import type { HelpcenterContentResponse } from "@deskpro-product/messenger-api-schemas/messenger/api/content/query";
import { useLauncherContext } from "../../../../lib/client/contexts/LauncherContext";

type HelpcenterContent = NonNullable<HelpcenterContentResponse["content"]>[number];

interface HcContent {
  type: HelpContentType;
  id: string;
}

export type HelpCenterContentState = {
  data?: HelpcenterContent;
  error?: string;
};

type HelpCenterContentContextType = {
  fetchHelpCenterContent: (...hcContent: HcContent[]) => {
    data: HelpCenterContentState[];
    pending: boolean;
  };
};

const HelpCenterContentContext = createContext<HelpCenterContentContextType>({
  fetchHelpCenterContent: () => ({ data: [], pending: false }),
});

function debounce<F extends (...args: Parameters<F>) => ReturnType<F>>(
  func: F,
  waitFor = 10,
): (...args: Parameters<F>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<F>): void => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), waitFor);
  };
}

export const useHelpCenterContext = () => useContext(HelpCenterContentContext);

export const HelpCenterProvider = ({ children }: { children: ReactNode }) => {
  const [helpCenterContent, setHelpCenterContent] = useState<{
    [T in HelpContentType]: { [id: string]: HelpCenterContentState };
  }>({
    article: {},
    community_topic: {},
    download: {},
    guide_page: {},
    news: {},
  });
  const helpCenterContentRequested = useRef<{ [T in HelpContentType]: Set<string> }>({
    article: new Set(),
    community_topic: new Set(),
    download: new Set(),
    guide_page: new Set(),
    news: new Set(),
  });
  const helpCenterContentQueued = useRef<HcContent[]>([]);
  const { api } = useLauncherContext();

  const downloadHelpCenterContent = useCallback(async () => {
    const queue = helpCenterContentQueued.current;
    helpCenterContentQueued.current = [];
    try {
      const { content: newContent } = await api.getHelpCenterContents({
        content: JSON.stringify(Array.from(queue)),
        categories: JSON.stringify([]),
      });
      if (newContent) {
        setHelpCenterContent((content) => {
          const clone = structuredClone(content);
          queue.forEach((i) => {
            clone[i.type][i.id] = {
              error: "You are denied permission to see this help center content...",
            };
          });
          newContent.forEach((c) => {
            clone[c.type][c.id] = {
              data: c,
            };
          });
          return clone;
        });
      }
    } catch (e) {
      setHelpCenterContent((content) => {
        const clone = structuredClone(content);
        queue.forEach((i) => {
          clone[i.type][i.id] = {
            error:
              e instanceof Error
                ? e.message
                : "An error occurred downloading help center content...",
          };
        });
        return clone;
      });
    }
  }, [api]);

  const debouncedDownloadHelpCenterContent = useMemo(
    () => debounce(downloadHelpCenterContent),
    [downloadHelpCenterContent],
  );

  const fetchHelpCenterContent = useCallback(
    (...hcContent: HcContent[]): { data: HelpCenterContentState[]; pending: boolean } => {
      const data: HelpCenterContentState[] = [];
      const notFound = new Set<HcContent>();
      hcContent.forEach((h) => {
        const content = helpCenterContent[h.type][h.id];
        if (content) {
          data.push(content);
        } else {
          notFound.add(h);
        }
      });
      if (notFound.size === 0) {
        return { data, pending: false };
      }
      notFound.forEach(({ type, id }) => {
        const contentRequested = helpCenterContentRequested.current[type].has(id);
        if (!contentRequested) {
          helpCenterContentRequested.current[type].add(id);
          helpCenterContentQueued.current.push({ type, id });
          debouncedDownloadHelpCenterContent();
        }
      });
      return { data, pending: true };
    },
    [helpCenterContent, debouncedDownloadHelpCenterContent],
  );
  const helpCenterContext = useMemo(() => ({ fetchHelpCenterContent }), [fetchHelpCenterContent]);
  return (
    <HelpCenterContentContext.Provider value={helpCenterContext}>
      {children}
    </HelpCenterContentContext.Provider>
  );
};
